import React, { useRef, useLayoutEffect, useState as useReactState } from 'react';
import styled, { useTheme } from 'styled-components';
import { Button, CardContainer, LayoutGrid as LayoutGridComponent, LayoutMain, LayoutSide, SectionHeading, StatusBadge, Icon } from '@tviklax/ui-components';
import type { IconName, StatusBadgeVariant } from '@tviklax/ui-components';
import { useGraphQLQuery } from '@tviklax/ui-components';
import { gql } from '@apollo/client';
import { useRole } from '../../context/RoleContext';
import { logAudit } from '../../shared/audit/audit';
import { ContactPanel } from '../../shared/components/ContactPanel/ContactPanel';
import { ContactCard } from '../../shared/components/ContactCard/ContactCard';
import { Contact } from '../../shared/types/contact';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => `${theme.spacing[5]} ${theme.spacing[6]}`};
`;

// Layout components now imported from ui-components

const Breadcrumb = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray600};
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Crumb = styled.span`
  color: ${({ theme }) => theme.colors.gray800};
`;

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 12;
  background: ${({ theme }) => theme.colors.background};
  backdrop-filter: blur(6px);
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: flex-start;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing[3]};
`;

const Label = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray600};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  font-weight: 700;
`;

const Value = styled.div`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.gray900};
  font-weight: 600;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing[3]};
`;

const ActionLink = styled.button<{ disabled?: boolean }>`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme, disabled }) => disabled ? theme.colors.gray400 : theme.colors.primary};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  text-decoration: none;
  white-space: nowrap;
  
  &:hover:not(:disabled) {
    text-decoration: underline;
  }
`;

const Inline = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: nowrap;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${({ theme }) => theme.spacing[3]};
`;

const TimelineItem = styled.div`
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
`;

const TimelineLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.gray600};
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.03em;
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
`;

const TimelineTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
`;

const TimelineValue = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray800};
  display: flex;
  flex-direction: column;
  gap: 8px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  min-width: 0;
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.gray200};
`;

type StepState = 'done' | 'active' | 'todo' | 'error';

const StepperRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${({ theme }) => theme.spacing[2]};

  @media (max-width: 640px) {
    grid-template-columns: repeat(5, minmax(60px, 1fr));
    overflow-x: auto;
  }
`;

const StepperItem = styled.div<{ $state: StepState }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 15px;
    left: calc(50% + 20px);
    width: calc(100% - 40px);
    height: 2px;
    background: ${({ theme, $state }) => ($state === 'done' ? theme.colors.success : theme.colors.borderSubtle)};
  }
`;

const StepCircle = styled.div<{ $state: StepState }>`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
  background: ${({ theme, $state }) => {
    if ($state === 'done') return theme.colors.success;
    if ($state === 'error') return theme.colors.error;
    return theme.colors.surface;
  }};
  color: ${({ theme, $state }) => {
    if ($state === 'done' || $state === 'error') return theme.colors.surface;
    if ($state === 'active') return theme.colors.primary;
    return theme.colors.textMuted;
  }};
  border: 2px solid ${({ theme, $state }) => {
    if ($state === 'done') return theme.colors.success;
    if ($state === 'active') return theme.colors.primary;
    if ($state === 'error') return theme.colors.error;
    return theme.colors.borderStrong;
  }};
`;

const StepLabel = styled.div<{ $state: StepState }>`
  font-size: 12px;
  font-weight: ${({ $state }) => ($state === 'active' || $state === 'error' ? 700 : 600)};
  color: ${({ theme, $state }) => {
    if ($state === 'active') return theme.colors.primary;
    if ($state === 'error') return theme.colors.error;
    if ($state === 'done') return theme.colors.gray800;
    return theme.colors.textMuted;
  }};
`;

const StepMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.gray600};
  font-size: 13px;
`;

const PeopleNote = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.gray500};
`;

const LockNotice = styled.div`
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.warningLight};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.warningDark};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
`;

const TableHead = styled.thead`
  border-bottom: 2px solid ${({ theme }) => theme.colors.gray200};
`;

const TableHeader = styled.th`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: 12px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.gray600};
  font-weight: 700;
  vertical-align: middle;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray100};
  transition: background-color 0.2s ease-in-out;
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray0};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  vertical-align: middle;
`;

const Badge = styled.span<{ $variant?: 'default' | 'info' }>`
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ theme, $variant }) => ($variant === 'info' ? theme.colors.infoLight : theme.colors.gray200)};
  color: ${({ theme, $variant }) => ($variant === 'info' ? theme.colors.infoDark : theme.colors.gray900)};
  font-size: 12px;
  font-weight: 700;
`;

const DeclarationBox = styled.div<{ $open: boolean }>`
  border: 1px dashed ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme, $open }) => ($open ? theme.colors.background : theme.colors.surface)};
  padding: ${({ theme }) => theme.spacing[3]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const Workspace = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Checklist = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const NoteCard = styled.div`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  flex-direction: column;
  gap: 6px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  min-width: 0;
`;

const Audience = styled.span`
  padding: 3px 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.infoLight};
  color: ${({ theme }) => theme.colors.infoDark};
  font-size: 12px;
  font-weight: 700;
`;

const Intent = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.gray700};
  max-width: 720px;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 12px;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  width: 100%;
  box-shadow: ${({ theme }) => theme.shadows.xl};
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.surface};
  z-index: 1;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.gray900};
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ModalFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  position: sticky;
  bottom: 0;
  background: ${({ theme }) => theme.colors.surface};
`;

export interface CustomsDeclarationPageProps {
  orderId: string;
  carrier: string;
  vessel: string;
  eta: string;
  selectedOrderId?: string;
  onOrderChange?: (orderId: string) => void;
  onBack?: () => void;
  onOpenMessaging?: (payload: { orderId?: string; targetContactId?: number; targetContactName?: string; targetContactEmail?: string; preferredSurface?: 'drawer' | 'widget' }) => void;
}

type CustomsOrder = {
  orderId: string;
  carrier: string;
  vessel: string;
  eta: string;
};

type DocumentRow = {
  name: string;
  version: string;
  status: 'approved' | 'filed' | 'pending' | 'optional';
  usedInFiling: boolean;
  triggeredBy: 'query' | 'voluntary' | 'system';
  linkedQueryId?: string;
};

type TimelineEvent = {
  id: string;
  timestamp: string;
  title: string;
  source: 'System' | 'Officer' | 'User';
  ref?: string;
  details: string;
  tone?: 'queried' | 'cleared' | 'filed' | 'assessment';
  impact?: string[];
  outcome?: 'status-changed' | 'sla-paused' | 'booking-unblocked' | 'no-action-required';
};

type CaseNote = {
  id: string;
  author: string;
  audience: 'internal' | 'customer';
  category: 'blocking' | 'info';
  summary: string;
  noteType: 'system' | 'customer-safe' | 'internal' | 'legal';
};

type CaseState =
  | 'DRAFT'
  | 'FILED'
  | 'UNDER_REVIEW'
  | 'QUERY_RAISED'
  | 'AWAITING_RESPONSE'
  | 'AMENDMENT_SUBMITTED'
  | 'REVIEW_RESUMED'
  | 'CLEARED'
  | 'REJECTED'
  | 'CANCELLED';

type Query = {
  id: string;
  queryId: string;
  title: string;
  blocking: boolean;
  status: 'open' | 'responded' | 'closed';
  raisedBy: string;
  dueBy?: string;
  details: string;
  eventType: 'query' | 'hold' | 'notification' | 'screening' | 'exam';
  domain: 'hs-classification' | 'valuation' | 'party' | 'routing' | 'security' | 'origin' | 'license';
  phase: 'pre-departure' | 'pre-arrival' | 'post-arrival';
};

type AssessmentPath = 'RMS_FACILITATED' | 'APPRAISING' | 'EXAMINATION' | 'PENDING';

type SubmissionStatus = {
  state: 'submitted' | 'queued' | 'failed' | 'idle';
  message?: string;
  lastRetry?: string;
};

type Authority = {
  code: string;
  country: string;
  countryFlag: string;
  filingMode: 'API' | 'EDI' | 'Broker';
  capabilities: {
    amendments: boolean;
    asyncQueries: boolean;
    partialVisibility: boolean;
  };
};

type ForexChecklistItem = {
  requirement: string;
  status: 'pending' | 'uploaded' | 'approved' | 'rejected';
  file?: string | null;
  notes?: string | null;
  required: boolean;
};

type ForexApprovalHistory = {
  timestamp: string;
  by: string;
  action: string;
  reason?: string | null;
  newStatus?: string | null;
};

type ForexComplianceRecord = {
  id: string;
  buyer: string;
  supplier: string;
  country: string;
  currency: string;
  totalValue: number;
  incoterms: string;
  expectedShipmentDate?: string | null;
  complianceStatus: string;
  checklist: ForexChecklistItem[];
  approvalHistory: ForexApprovalHistory[];
  bankMetadata: {
    adBankName?: string | null;
    adBankCode?: string | null;
    femaReference?: string | null;
    bankSubmissionRef?: string | null;
  };
  contacts?: Array<{
    id: number;
    name: string;
    organization?: string | null;
    role?: string | null;
    team?: string | null;
    email?: string | null;
    phone?: string | null;
    visibility?: string | null;
    isPrimary?: boolean | null;
    assignmentRole?: string | null;
  }>;
};

const CUSTOMS_FOREX_QUERY = gql`
  query CustomsForexRecords($limit: Int!, $offset: Int!) {
    forexComplianceRecords(limit: $limit, offset: $offset) {
      id
      buyer
      supplier
      country
      currency
      totalValue
      incoterms
      expectedShipmentDate
      complianceStatus
      checklist {
        requirement
        status
        file
        notes
        required
      }
      approvalHistory {
        timestamp
        by
        action
        reason
        newStatus
      }
      bankMetadata {
        adBankName
        adBankCode
        femaReference
        bankSubmissionRef
      }
      contacts {
        id
        name
        organization
        role
        team
        email
        phone
        visibility
        isPrimary
        assignmentRole
      }
    }
  }
`;

const authority: Authority = {
  code: 'ICEGATE',
  country: 'India',
  countryFlag: '🇮🇳',
  filingMode: 'API',
  capabilities: {
    amendments: true,
    asyncQueries: true,
    partialVisibility: true,
  },
};

const assessmentPathDisplay: Record<AssessmentPath, string> = {
  RMS_FACILITATED: 'RMS (Facilitated)',
  APPRAISING: 'Appraising → Officer Review',
  EXAMINATION: 'Appraising → Physical Examination',
  PENDING: 'Assessment Pending',
};

const normalizeCustomsContactRole = (role?: string | null): Contact['role'] => {
  switch ((role || '').toLowerCase()) {
    case 'customer':
      return 'Customer';
    case 'manufacturer':
      return 'Manufacturer';
    case 'carrier':
      return 'Carrier';
    case 'customs broker':
    case 'customs_broker':
      return 'Customs Broker';
    case 'compliance officer':
    case 'compliance_officer':
      return 'Compliance Officer';
    case 'finance contact':
    case 'finance_contact':
      return 'Finance Contact';
    case 'authority':
      return 'Authority';
    default:
      return 'Ops Owner';
  }
};

const mapForexApiContactToContact = (orderKey: string, raw: NonNullable<ForexComplianceRecord['contacts']>[number]): Contact => ({
  id: String(raw.id),
  name: raw.name || '',
  organization: raw.organization || undefined,
  role: normalizeCustomsContactRole(raw.role),
  email: raw.email || '',
  phone: raw.phone || undefined,
  visibility: raw.visibility === 'Customer-visible' ? 'Customer-visible' : 'Internal',
  primary: Boolean(raw.isPrimary),
  linkedOrders: [orderKey],
  linkedCustomsCases: [orderKey],
});

const caseStateDisplay: Record<CaseState, { label: string; variant: StatusBadgeVariant }> = {
  DRAFT: { label: 'Draft', variant: 'pending' },
  FILED: { label: 'Submitted to Authority', variant: 'filed' },
  UNDER_REVIEW: { label: 'Under Assessment', variant: 'info' },
  QUERY_RAISED: { label: 'Blocking Query Raised', variant: 'warning' },
  AWAITING_RESPONSE: { label: 'Awaiting Your Response', variant: 'pending' },
  AMENDMENT_SUBMITTED: { label: 'Amendment Submitted to Authority', variant: 'filed' },
  REVIEW_RESUMED: { label: 'Authority Re-assessing', variant: 'info' },
  CLEARED: { label: 'Legally Cleared (LEO Granted)', variant: 'approved' },
  REJECTED: { label: 'Rejected', variant: 'error' },
  CANCELLED: { label: 'Cancelled', variant: 'neutral' },
};

const DOC_STATUS_BADGE: Record<DocumentRow['status'], { variant: StatusBadgeVariant; label: string }> = {
  approved: { variant: 'approved', label: 'Approved' },
  filed: { variant: 'filed', label: 'Filed' },
  pending: { variant: 'pending', label: 'Pending' },
  optional: { variant: 'neutral', label: 'Optional' },
};

const SUBMISSION_STATUS_BADGE: Record<'submitted' | 'queued' | 'failed', { variant: StatusBadgeVariant; label: string }> = {
  submitted: { variant: 'filed', label: 'Submitted' },
  queued: { variant: 'pending', label: 'Queued (Retry in progress)' },
  failed: { variant: 'error', label: 'Failed' },
};

const TIMELINE_TONE_META: Record<NonNullable<TimelineEvent['tone']>, { variant: StatusBadgeVariant; icon: IconName; label: string }> = {
  cleared: { variant: 'approved', icon: 'CheckCircle', label: 'LEGALLY CLEARED (LEO)' },
  queried: { variant: 'warning', icon: 'AlertTriangle', label: 'BLOCKING QUERY RAISED' },
  filed: { variant: 'filed', icon: 'Send', label: 'SUBMITTED TO AUTHORITY' },
  assessment: { variant: 'info', icon: 'FileText', label: 'INFORMATIONAL REMARK' },
};

const OUTCOME_BADGE_META: Record<NonNullable<TimelineEvent['outcome']>, { variant: StatusBadgeVariant; icon?: IconName; label: string }> = {
  'status-changed': { variant: 'info', label: 'Status Changed' },
  'sla-paused': { variant: 'warning', icon: 'Clock', label: 'SLA Paused' },
  'booking-unblocked': { variant: 'approved', icon: 'CheckCircle', label: 'Booking Unblocked' },
  'no-action-required': { variant: 'neutral', icon: 'CheckCircle', label: 'No Action Required' },
};

// Condensed 5-step milestone view of the full CaseState progression above —
// the query/amendment/re-assessment loop collapses into a single
// "Query Resolution" step since those states all represent the same
// milestone (case is stuck pending a response) from a glance-and-go view.
const CASE_STEPS: { key: string; label: string }[] = [
  { key: 'draft', label: 'Draft' },
  { key: 'filed', label: 'Filed' },
  { key: 'review', label: 'Under Assessment' },
  { key: 'query', label: 'Query Resolution' },
  { key: 'cleared', label: 'Cleared' },
];

const caseStateStepIndex = (state: CaseState): number => {
  switch (state) {
    case 'DRAFT':
      return 0;
    case 'FILED':
      return 1;
    case 'UNDER_REVIEW':
      return 2;
    case 'QUERY_RAISED':
    case 'AWAITING_RESPONSE':
    case 'AMENDMENT_SUBMITTED':
    case 'REVIEW_RESUMED':
      return 3;
    case 'CLEARED':
    case 'REJECTED':
    case 'CANCELLED':
      return 4;
    default:
      return 0;
  }
};

export const CustomsDeclarationPage: React.FC<CustomsDeclarationPageProps> = ({ orderId, carrier, vessel, eta, selectedOrderId: controlledOrderId, onOrderChange, onBack, onOpenMessaging }) => {
  const { data: forexData } = useGraphQLQuery<{ forexComplianceRecords: ForexComplianceRecord[] }>(CUSTOMS_FOREX_QUERY, {
    variables: { limit: 300, offset: 0 },
    showErrorToast: false,
    fetchPolicy: 'network-only',
    pollInterval: 4000,
  });
  const liveOrders = React.useMemo<CustomsOrder[]>(() => {
    const records = forexData?.forexComplianceRecords || [];
    if (!records.length) {
      return [{
        orderId,
        carrier,
        vessel,
        eta,
      }];
    }
    return records.map((record) => ({
      orderId: record.id,
      carrier: carrier || record.bankMetadata?.adBankName || 'Pending carrier assignment',
      vessel: vessel || record.supplier || 'Pending vessel assignment',
      eta: record.expectedShipmentDate || eta || 'TBD',
    }));
  }, [forexData?.forexComplianceRecords, orderId, carrier, vessel, eta]);

  const [localOrderId, setLocalOrderId] = React.useState(orderId || liveOrders[0]?.orderId || '');
  const selectedOrderId = controlledOrderId ?? localOrderId;
  
  const handleOrderChange = (orderId: string) => {
    setLocalOrderId(orderId);
    onOrderChange?.(orderId);
  };
  
  const { role: userRole, can } = useRole();
  const theme = useTheme();
  const [openDeclaration, setOpenDeclaration] = React.useState(false);
  const [showWorkspace, setShowWorkspace] = React.useState(false);
  const [selectedQuery, setSelectedQuery] = React.useState<Query | null>(null);
  const [showDuties, setShowDuties] = React.useState(false);
  const [showFullDeclaration, setShowFullDeclaration] = React.useState(false);
  const [showAuthorityDetails, setShowAuthorityDetails] = React.useState(false);
  const [showUploadModal, setShowUploadModal] = React.useState(false);
  const [selectedUploadFile, setSelectedUploadFile] = React.useState<File | null>(null);
  const [selectedDocument, setSelectedDocument] = React.useState<DocumentRow | null>(null);
  const [showDocumentViewer, setShowDocumentViewer] = React.useState(false);
  const [showVersionHistory, setShowVersionHistory] = React.useState(false);

  const headerRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const [railTop, setRailTop] = useReactState<number>(120);
  useLayoutEffect(() => {
    const updateTop = () => {
      const h = headerRef.current?.getBoundingClientRect().bottom || 0;
      setRailTop(h + 12);
    };
    updateTop();
    window.addEventListener('resize', updateTop);
    return () => window.removeEventListener('resize', updateTop);
  }, []);

  const selectedOrder = React.useMemo(() => {
    const match = liveOrders.find((o) => o.orderId === selectedOrderId);
    if (match) return match;
    return { orderId, carrier, vessel, eta };
  }, [selectedOrderId, liveOrders, orderId, carrier, vessel, eta]);

  const selectedRecord = React.useMemo(
    () => (forexData?.forexComplianceRecords || []).find((record) => record.id === selectedOrder.orderId),
    [forexData?.forexComplianceRecords, selectedOrder.orderId],
  );

  const statusToCaseState = (status?: string | null): CaseState => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'draft') return 'DRAFT';
    if (normalized === 'submitted' || normalized === 'docs-resubmitted') return 'FILED';
    if (normalized === 'docs-requested') return 'QUERY_RAISED';
    if (normalized === 'compliance-approved') return 'CLEARED';
    if (normalized === 'remittance-initiated' || normalized === 'remittance-completed' || normalized === 'locked') return 'CLEARED';
    return 'UNDER_REVIEW';
  };

  const caseState: CaseState = statusToCaseState(selectedRecord?.complianceStatus);
  const statusMeta = caseStateDisplay[caseState];

  const documents: DocumentRow[] = React.useMemo(() => {
    const checklist = selectedRecord?.checklist || [];
    const mappedDocs: DocumentRow[] = checklist.map((item) => ({
      name: item.requirement,
      version: item.file ? 'v1' : '—',
      status:
        item.status === 'approved'
          ? 'approved'
          : item.status === 'uploaded'
            ? 'filed'
            : item.required
              ? 'pending'
              : 'optional',
      usedInFiling: item.status === 'approved' || item.status === 'uploaded',
      triggeredBy: item.notes ? 'query' : 'system',
      linkedQueryId: item.notes ? 'AUTO-NOTE' : undefined,
    }));

    // Ensure core customs documents exist if the checklist is missing them
    const coreDocs = [
      { name: 'Shipping Bill', required: true },
      { name: 'Commercial Invoice', required: true },
      { name: 'Packing List', required: true },
      { name: 'Bill of Lading / Airway Bill', required: true }
    ];

    coreDocs.forEach(coreDoc => {
      const exists = mappedDocs.some(d => 
        d.name.toLowerCase().includes(coreDoc.name.toLowerCase()) || 
        (coreDoc.name === 'Shipping Bill' && d.name.toLowerCase().includes('bill of entry'))
      );
      if (!exists) {
        mappedDocs.unshift({
          name: coreDoc.name,
          version: '—',
          status: 'pending',
          usedInFiling: false,
          triggeredBy: 'system',
          linkedQueryId: undefined,
        });
      }
    });

    return mappedDocs;
  }, [selectedRecord?.checklist]);

  const requiredDocuments = React.useMemo(() => documents.filter((d) => d.status !== 'optional'), [documents]);
  const approvedRequiredDocuments = React.useMemo(
    () => requiredDocuments.filter((d) => d.status === 'approved' || d.status === 'filed'),
    [requiredDocuments],
  );

  const timeline: TimelineEvent[] = React.useMemo(() => {
    const history = selectedRecord?.approvalHistory || [];
    return history.map((event, index) => {
      const action = String(event.action || 'Update');
      const lowered = action.toLowerCase();
      const tone: TimelineEvent['tone'] =
        lowered.includes('approve') || lowered.includes('clear')
          ? 'cleared'
          : lowered.includes('request') || lowered.includes('reject')
            ? 'queried'
            : lowered.includes('submit')
              ? 'filed'
              : 'assessment';
      return {
        id: `${selectedOrder.orderId}-evt-${index}`,
        timestamp: event.timestamp || new Date().toISOString(),
        title: action,
        source: event.by?.toLowerCase().includes('officer') ? 'Officer' : event.by?.toLowerCase().includes('system') ? 'System' : 'User',
        ref: event.newStatus || undefined,
        details: event.reason || `Status updated to ${event.newStatus || selectedRecord?.complianceStatus || 'in-progress'}`,
        tone,
        impact: tone === 'queried' ? ['Requires compliance action'] : tone === 'cleared' ? ['Customs clearance progressing'] : [],
        outcome: tone === 'queried' ? 'status-changed' : tone === 'cleared' ? 'booking-unblocked' : 'no-action-required',
      };
    });
  }, [selectedRecord?.approvalHistory, selectedOrder.orderId, selectedRecord?.complianceStatus]);

  const queries: Query[] = [];
  const activeQueries = queries.filter((q) => q.status === 'open');

  const assessmentPath: AssessmentPath = caseState === 'QUERY_RAISED' ? 'APPRAISING' : caseState === 'CLEARED' ? 'RMS_FACILITATED' : 'PENDING';
  const submissionStatus: SubmissionStatus = {
    state: selectedRecord ? (caseState === 'DRAFT' ? 'idle' : 'submitted') : 'idle',
    message: selectedRecord ? `Compliance status: ${selectedRecord.complianceStatus}` : 'No compliance record',
  };
  const lastUpdatedValue = timeline[0]?.timestamp || '—';
  const totalValueAmount = selectedRecord?.totalValue || 0;
  const totalValueDisplay = selectedRecord
    ? `${selectedRecord.currency || 'USD'} ${Number(totalValueAmount).toLocaleString()}`
    : '—';
  const dutyAmountDisplay = selectedRecord
    ? `${selectedRecord.currency || 'USD'} ${(Number(totalValueAmount) * 0.06).toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : '—';

  const canRespond = can('customs.respond');
  const canView = can('customs.view');
  const canUploadDocs = can('documents.upload');

  const notes: CaseNote[] = React.useMemo(() => {
    if (!selectedRecord) {
      return [{
        id: 'note-empty',
        author: 'System',
        audience: 'customer',
        category: 'info',
        summary: 'No customs compliance record is linked to this order yet.',
        noteType: 'system',
      }];
    }
    return [
      {
        id: 'note-status',
        author: 'System',
        audience: 'customer',
        category: caseState === 'QUERY_RAISED' ? 'blocking' : 'info',
        summary: `Current compliance status: ${selectedRecord.complianceStatus}`,
        noteType: 'customer-safe',
      },
      {
        id: 'note-owner',
        author: 'Compliance Team',
        audience: 'internal',
        category: 'info',
        summary: `Documents tracked: ${documents.length}. Required approvals are monitored in the checklist.`,
        noteType: 'internal',
      },
    ];
  }, [selectedRecord, caseState, documents.length]);

  const visibleNotes = notes.filter((note) => (userRole === 'customer' ? note.audience === 'customer' : true));

   // Map real API contacts from GraphQL query
   const apiContacts = (selectedRecord?.contacts || []).map((contact) => mapForexApiContactToContact(selectedOrder.orderId, contact));
   const contacts: { complianceOfficer?: Contact; broker?: Contact } = {
     complianceOfficer: apiContacts.find((c) => (c.role || '').toLowerCase().includes('compliance officer')) || undefined,
     broker: apiContacts.find((c) => (c.role || '').toLowerCase().includes('customs broker')) || undefined,
   };
  const complianceOwnerName = contacts.complianceOfficer?.name || '—';
  const peopleCount = [contacts.complianceOfficer, contacts.broker].filter(Boolean).length;

  return (
    <>
    <Page>
      <TopRow ref={headerRef}>
        <div>
          <Breadcrumb>
            <Crumb>Order {selectedOrder.orderId}</Crumb>
            <span>→</span>
            <Crumb>Shipping</Crumb>
            <span>→</span>
            <Crumb>Customs Case Manager</Crumb>
          </Breadcrumb>
          <Intent>
            This is a customs case manager: status, ownership, blockers, and what changed — not a filing form.
          </Intent>
          <Inline style={{ marginTop: 8, gap: 24 }}>
            <div>
              <Label>Total FOB Value</Label>
              <Value>{totalValueDisplay}</Value>
            </div>
            <div>
              <Label>Est. Duty</Label>
              <Value>{dutyAmountDisplay}</Value>
            </div>
          </Inline>
          <PeopleNote>
            <Icon name="Users" size={14} />
            <span>People involved: {peopleCount}</span>
            <Button variant="ghost" size="sm" onClick={() => {
              if (railRef.current) {
                railRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                railRef.current.focus({ preventScroll: true });
              }
            }} style={{ marginLeft: 8 }}>
              View people →
            </Button>
          </PeopleNote>
        </div>
        <Inline>
          {!canRespond && (
            <LockNotice>
              <Icon name="Lock" size={14} />
              Only Compliance can respond to customs queries
            </LockNotice>
          )}
          {onBack && (
            <Button size="sm" variant="ghost" onClick={onBack}>
              ← Back to Scheduling
            </Button>
          )}
        </Inline>
      </TopRow>

      <StickyHeader>
        <Inline>
          <div style={{ minWidth: 220 }}>
            <Label>Select Order</Label>
            <select
              value={selectedOrderId}
              onChange={(e) => handleOrderChange(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 10, border: `1px solid ${theme.colors.borderStrong}`, width: '100%' }}
            >
              {liveOrders.map((o) => (
                <option key={o.orderId} value={o.orderId}>
                  {o.orderId}
                </option>
              ))}
            </select>
          </div>
          <Muted>Switching updates status, documents, and timeline for that shipment.</Muted>
        </Inline>
        <SummaryGrid>
          <div>
            <Label>Shipment / Order</Label>
            <Value>{selectedOrder.orderId}</Value>
          </div>
          <div>
            <Label>Country</Label>
            <Inline>
              <span aria-hidden>{authority.countryFlag}</span>
              <Value>{authority.country}</Value>
            </Inline>
          </div>
          <div>
            <Label>Authority</Label>
            <Inline>
              <Value>{authority.code}</Value>
              <Badge $variant="info">{authority.filingMode}</Badge>
            </Inline>
          </div>
          <div>
            <Label>Assessment Path</Label>
            <Value>
              <Badge $variant="info">{assessmentPathDisplay[assessmentPath]}</Badge>
            </Value>
          </div>
          <div>
            <Label>Port of Export</Label>
            <Value>{selectedRecord?.country || '—'}</Value>
          </div>
          <div>
            <Label>Mode</Label>
            <Value>Export</Value>
          </div>
          <div>
            <Label>Customs Status</Label>
            <Value>
              <StatusBadge variant={statusMeta.variant} label={statusMeta.label} />
            </Value>
          </div>
          <div>
            <Label>Case Owner</Label>
            <Value>{complianceOwnerName}</Value>
          </div>
          <div>
            <Label>Last Updated</Label>
            <Value>{lastUpdatedValue}</Value>
          </div>
          <div>
            <Label>Submission Status</Label>
            <Value>
              {submissionStatus.state === 'idle' ? (
                <span>—</span>
              ) : (
                <StatusBadge
                  variant={SUBMISSION_STATUS_BADGE[submissionStatus.state].variant}
                  label={SUBMISSION_STATUS_BADGE[submissionStatus.state].label}
                />
              )}
            </Value>
          </div>
          {submissionStatus.state === 'queued' && submissionStatus.lastRetry && (
            <div>
              <Label>Last Retry</Label>
              <Value>{submissionStatus.lastRetry}</Value>
            </div>
          )}
          <div>
            <Label>Export Declaration No.</Label>
            <Value>{selectedRecord?.bankMetadata?.bankSubmissionRef || '—'}</Value>
          </div>
          <div>
            <Label>Authority Ref</Label>
            <Value>{selectedRecord?.bankMetadata?.femaReference || '—'}</Value>
          </div>
        </SummaryGrid>
      </StickyHeader>

      <LayoutGridComponent mainRatio="calc(100% - 380px)" sideRatio="360px" gap="20px">
        <LayoutMain>
          <Card>
        <HeaderRow>
          <div>
            <Label>Carrier</Label>
            <Value>{selectedOrder.carrier}</Value>
          </div>
          <div>
            <Label>Vessel</Label>
            <Value>{selectedOrder.vessel}</Value>
          </div>
          <div>
            <Label>ETA</Label>
            <Value>{selectedOrder.eta}</Value>
          </div>
        </HeaderRow>
      </Card>

      <Card>
        <SectionHeading icon="Zap">Is Customs Blocking Your Shipment?</SectionHeading>
        <Muted style={{ fontSize: '13px', marginTop: '4px' }}>Active queries and their impact on booking, dispatch, and timeline</Muted>
        <StepperRow style={{ marginTop: 8 }}>
          {CASE_STEPS.map((step, index) => {
            const activeIndex = caseStateStepIndex(caseState);
            const isTerminalError = caseState === 'REJECTED' || caseState === 'CANCELLED';
            const state: StepState =
              index < activeIndex ? 'done' : index === activeIndex ? (isTerminalError ? 'error' : 'active') : 'todo';
            const label = index === activeIndex && isTerminalError ? caseStateDisplay[caseState].label : step.label;
            return (
              <StepperItem key={step.key} $state={state}>
                <StepCircle $state={state}>
                  {state === 'done' && <Icon name="CheckCircle" size={16} />}
                  {state === 'error' && <Icon name="XCircle" size={16} />}
                  {(state === 'active' || state === 'todo') && index + 1}
                </StepCircle>
                <StepLabel $state={state}>{label}</StepLabel>
                {index === activeIndex && <StepMeta>{lastUpdatedValue !== '—' ? lastUpdatedValue : 'In progress'}</StepMeta>}
              </StepperItem>
            );
          })}
        </StepperRow>
        <Inline style={{ marginTop: '12px' }}>
          <StatusBadge variant={statusMeta.variant} label={statusMeta.label} />
          {activeQueries.length > 0 && (
            <Badge>({activeQueries.length} {activeQueries.length === 1 ? 'query' : 'queries'} to resolve)</Badge>
          )}
        </Inline>
        <Divider />
        {activeQueries.length > 0 ? (
          <>
            <SectionHeading size="sm">Active Queries</SectionHeading>
            <TwoCol>
              {queries.map((query) => (
                <NoteCard key={query.id}>
                  <Inline>
                    <strong>{query.queryId}</strong>
                    {query.blocking && <StatusBadge variant="error" label="Blocking" />}
                    {query.status === 'open' && <StatusBadge variant="warning" label="Action Required" />}
                    {query.status === 'responded' && <StatusBadge variant="info" label="Responded" />}
                    {query.status === 'closed' && <StatusBadge variant="approved" label="Resolved" />}
                  </Inline>
                  <Value>{query.title}</Value>
                  <Inline>
                    <Badge $variant="info">
                      {query.domain === 'hs-classification' && 'HS Classification'}
                      {query.domain === 'valuation' && 'Valuation'}
                      {query.domain === 'party' && 'Party Details'}
                      {query.domain === 'routing' && 'Routing / Port'}
                      {query.domain === 'security' && 'Security Data'}
                      {query.domain === 'origin' && 'Origin / FTA'}
                      {query.domain === 'license' && 'License / PGA'}
                    </Badge>
                    {query.phase && (
                      <Badge>
                        {query.phase === 'pre-departure' && (<><Icon name="Upload" size={12} /> Pre-Departure</>)}
                        {query.phase === 'pre-arrival' && (<><Icon name="Send" size={12} /> Pre-Arrival</>)}
                        {query.phase === 'post-arrival' && (<><Icon name="Package" size={12} /> Post-Arrival</>)}
                      </Badge>
                    )}
                  </Inline>
                  <Muted>Raised by: {query.raisedBy}</Muted>
                  {query.dueBy && <Muted>Deadline: {query.dueBy}</Muted>}
                  <div>{query.details}</div>
                  {query.blocking && (
                    <Muted style={{ color: theme.colors.errorDark, fontSize: '12px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Icon name="AlertTriangle" size={12} /> Blocks booking and dispatch until resolved
                    </Muted>
                  )}
                  {!query.blocking && (
                    <Muted style={{ color: theme.colors.successDark, fontSize: '12px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Icon name="CheckCircle" size={12} /> Informational — Does not delay shipment. No urgency.
                    </Muted>
                  )}
                  {query.status === 'open' && (
                    <Inline>
                      <Button size="sm" disabled={!canRespond} title={!canRespond ? 'Only Compliance can respond to queries' : ''} onClick={() => { setSelectedQuery(query); setShowWorkspace(true); }}>
                        Resolve Query
                      </Button>
                      {!canRespond && (
                        <Muted title="This action requires Compliance role due to authority submission rules.">
                          Handled by Compliance · You'll be notified when resolved
                        </Muted>
                      )}
                    </Inline>
                  )}
                </NoteCard>
              ))}
            </TwoCol>
          </>
        ) : (
          <Muted>No active queries. Case is progressing normally.</Muted>
        )}
        {activeQueries.length > 0 && (
          <>
            <Divider />
            <div style={{ padding: '12px 0' }}>
              <Label>What Happens Next</Label>
              <ul style={{ margin: '8px 0 0 20px', paddingLeft: 0, fontSize: '13px', color: theme.colors.gray600, lineHeight: 1.6 }}>
                <li>Authority reviews your submission within 24–48 hours</li>
                <li>Booking and dispatch remain blocked until query is resolved</li>
                <li>You'll receive a notification when cleared or if additional information is needed</li>
              </ul>
            </div>
          </>
        )}
      </Card>

      <Card>
        <SectionHeading icon="FileText">Declaration Summary</SectionHeading>
        <DeclarationBox $open={openDeclaration}>
          <Inline>
            <Button size="sm" variant="outline" onClick={() => setOpenDeclaration((prev) => !prev)}>
              {openDeclaration ? 'Hide Key Fields' : 'Show Key Fields'}
            </Button>
            <Muted>Read-only snapshot; full filing lives elsewhere.</Muted>
          </Inline>
          {openDeclaration && (
            <Grid>
              <div>
                <Label>Exporter IEC</Label>
                <Value>{selectedRecord?.id || '—'}</Value>
              </div>
              <div>
                <Label>Importer</Label>
                <Value>{selectedRecord?.buyer || '—'}</Value>
              </div>
              <div>
                <Label>HS Codes</Label>
                <Value>Refer linked checklist documents</Value>
              </div>
              <div>
                <Label>Total FOB</Label>
                <Value>{totalValueDisplay}</Value>
              </div>
              <div>
                <Label>Currency</Label>
                <Value>{selectedRecord?.currency || '—'}</Value>
              </div>
              <div>
                <Label>Ports</Label>
                <Value>Loaded from linked shipment data</Value>
              </div>
              <div>
                <Label>Incoterms</Label>
                <Value>{selectedRecord?.incoterms || '—'}</Value>
              </div>
              <div>
                <Label>Drawback / GST</Label>
                <Value>Refer customs filing response</Value>
              </div>
            </Grid>
          )}
          <Inline>
            <Button size="sm" variant="ghost" disabled={!canView} title={!canView ? 'Compliance role required' : ''} onClick={() => setShowFullDeclaration(true)}>
              View Full Declaration
            </Button>
            <Button size="sm" variant="ghost" disabled={!canView} title={!canView ? 'Compliance role required' : ''} onClick={() => {
              const declaration = {
                exporterIEC: selectedRecord?.id || null,
                importer: selectedRecord?.buyer || null,
                items: documents.map(d => ({ document: d.name, version: d.version })),
                totalFOB: totalValueDisplay,
                authority: authority.code
              };
              const blob = new Blob([JSON.stringify(declaration, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `declaration-${selectedOrder.orderId}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}>
              Download Filed JSON
            </Button>
            <Button size="sm" variant="ghost" style={{ background: theme.colors.infoLight, color: theme.colors.infoDark, border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setShowAuthorityDetails(true)}>
              <Icon name="Search" size={14} /> View {authority.code} Details
            </Button>
            {!canRespond && <Muted>Only Compliance can respond to queries.</Muted>}
          </Inline>
        </DeclarationBox>
      </Card>

      <Card>
        <SectionHeading icon="DollarSign">Duties &amp; Taxes (Summary)</SectionHeading>
        <Inline>
          <Button size="sm" variant="outline" onClick={() => setShowDuties((prev) => !prev)}>
            {showDuties ? 'Hide' : 'Show'} Duty Breakdown
          </Button>
          <Muted>Future-proofed for payments and reconciliation; hidden for customers.</Muted>
        </Inline>
        {showDuties && (
          <Grid>
            <div>
              <Label>Assessable Value</Label>
              <Value>{totalValueDisplay}</Value>
            </div>
            <div>
              <Label>Duty Amount</Label>
              <Value>{dutyAmountDisplay}</Value>
            </div>
            <div>
              <Label>IGST</Label>
              <Value>$0 (not paid)</Value>
            </div>
            <div>
              <Label>Challan Status</Label>
              <Value>Generated · Pending confirmation</Value>
            </div>
            <div>
              <Label>Duty Collection</Label>
              <Value>Collected by: Customs Authority</Value>
            </div>
            <div>
              <Label>Payment Channel</Label>
              <Value>Authority Portal / Deferred Account</Value>
            </div>
            <div>
              <Label>Payment Required</Label>
              <Value>No payment required from customer</Value>
            </div>
          </Grid>
        )}
      </Card>

      <Card>
        <SectionHeading
          icon="FileText"
          action={
            <StatusBadge
              variant={approvedRequiredDocuments.length === requiredDocuments.length ? 'approved' : 'pending'}
              label={`${approvedRequiredDocuments.length} of ${requiredDocuments.length} required docs approved`}
            />
          }
        >
          Documents Used for Filing
        </SectionHeading>
        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeader>Document</TableHeader>
                <TableHeader>Version</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Used in Filing</TableHeader>
                <TableHeader>Triggered By</TableHeader>
                <TableHeader>Linked Query</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </TableHead>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.name}>
                  <TableCell>
                    <Value>{doc.name}</Value>
                  </TableCell>
                  <TableCell>
                    <Value>{doc.version}</Value>
                  </TableCell>
                  <TableCell>
                    <Value>
                      <StatusBadge variant={DOC_STATUS_BADGE[doc.status].variant} label={DOC_STATUS_BADGE[doc.status].label} />
                    </Value>
                  </TableCell>
                  <TableCell>
                    <Value>{doc.usedInFiling ? <Icon name="CheckCircle" size={16} /> : '—'}</Value>
                  </TableCell>
                  <TableCell>
                    <Value>
                      {doc.triggeredBy === 'query' && 'Query'}
                      {doc.triggeredBy === 'voluntary' && 'Voluntary'}
                      {doc.triggeredBy === 'system' && 'System'}
                    </Value>
                  </TableCell>
                  <TableCell>
                    <Value>{doc.linkedQueryId || '—'}</Value>
                  </TableCell>
                  <TableCell>
                    <Inline>
                      {doc.status !== 'pending' && (
                        <ActionLink onClick={() => {
                          setSelectedDocument(doc);
                          setShowDocumentViewer(true);
                        }}>View</ActionLink>
                      )}
                      
                      {(() => {
                        const isSystemDoc = doc.name.toLowerCase().includes('shipping bill') || doc.name.toLowerCase().includes('bill of entry');
                        
                        if (isSystemDoc && doc.status === 'pending') {
                          return (
                            <ActionLink disabled title="This document will be automatically fetched from ICEGATE/Customs">
                              Auto-Fetches
                            </ActionLink>
                          );
                        }

                        return (
                          <ActionLink disabled={!canUploadDocs} title={!canUploadDocs ? 'Compliance role required to modify documents' : ''} onClick={() => {
                            setSelectedDocument(doc);
                            setShowUploadModal(true);
                          }}>
                            {doc.status === 'pending' ? 'Upload' : 'Replace'}
                          </ActionLink>
                        );
                      })()}

                      {doc.status !== 'pending' && (
                        <ActionLink disabled={!canView} title={!canView ? 'Compliance role required' : ''} onClick={() => {
                          setSelectedDocument(doc);
                          setShowVersionHistory(true);
                        }}>
                          History
                        </ActionLink>
                      )}
                    </Inline>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!canUploadDocs && (
          <Muted style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icon name="Lock" size={12} /> Document uploads are restricted to Compliance.
          </Muted>
        )}
      </Card>

      <Card>
        <SectionHeading icon="History">Customs Case Timeline</SectionHeading>
        <TwoCol>
          {timeline.map((event) => (
            <TimelineItem key={event.id}>
              <TimelineHeader>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <TimelineTitle>{event.title}</TimelineTitle>
                  <TimelineLabel>{event.timestamp}</TimelineLabel>
                </div>
                {event.tone && (
                  <Inline style={{ gap: 6 }}>
                    <Icon name={TIMELINE_TONE_META[event.tone].icon} size={14} />
                    <StatusBadge variant={TIMELINE_TONE_META[event.tone].variant} label={TIMELINE_TONE_META[event.tone].label} />
                  </Inline>
                )}
              </TimelineHeader>
              <TimelineValue>
                <Muted>
                  By: {event.source === 'System' ? 'ICEGATE System' : event.source === 'Officer' ? 'Customs Officer' : `Compliance - ${complianceOwnerName}`}
                </Muted>
                {event.ref && <Muted>Ref: {event.ref}</Muted>}
                <div>{event.details}</div>
                {event.impact && event.impact.length > 0 && (
                  <Inline style={{ marginTop: 4 }}>
                    {event.impact.map((impact) => (
                      <Badge key={impact}>{impact}</Badge>
                    ))}
                  </Inline>
                )}
                {event.outcome && (
                  <Inline style={{ marginTop: 6 }}>
                    <StatusBadge variant={OUTCOME_BADGE_META[event.outcome].variant} label={OUTCOME_BADGE_META[event.outcome].label} />
                  </Inline>
                )}
              </TimelineValue>
            </TimelineItem>
          ))}
        </TwoCol>
      </Card>

      {showWorkspace && selectedQuery && (
        <Card>
          <Inline>
            <div>
              <SectionHeading icon="AlertTriangle">Query Resolution Workspace</SectionHeading>
              <Muted style={{ fontSize: '13px', marginTop: '4px' }}>Changes here are logged, versioned, and submitted directly to the authority.</Muted>
            </div>
            <Button size="sm" variant="ghost" onClick={() => { setShowWorkspace(false); setSelectedQuery(null); }}>Close</Button>
          </Inline>
          <Workspace>
            <div>
              <Label>Active Query</Label>
              <Inline>
                <Value>{selectedQuery.queryId}</Value>
                {selectedQuery.blocking && <StatusBadge variant="error" label="Blocking" />}
              </Inline>
              <SectionHeading size="sm" style={{ marginTop: '8px' }}>{selectedQuery.title}</SectionHeading>
              <Muted>Authority: {authority.code} · Raised by: {selectedQuery.raisedBy}</Muted>
              {selectedQuery.dueBy && <Muted>Deadline: {selectedQuery.dueBy}</Muted>}
              <Inline style={{ marginTop: '8px' }}>
                <Badge $variant="info">
                  {selectedQuery.domain === 'hs-classification' && 'HS Classification'}
                  {selectedQuery.domain === 'valuation' && 'Valuation'}
                  {selectedQuery.domain === 'party' && 'Party Details'}
                  {selectedQuery.domain === 'routing' && 'Routing / Port'}
                  {selectedQuery.domain === 'security' && 'Security Data'}
                  {selectedQuery.domain === 'origin' && 'Origin / FTA'}
                  {selectedQuery.domain === 'license' && 'License / PGA'}
                </Badge>
                <Badge>
                  {selectedQuery.phase === 'pre-departure' && (<><Icon name="Upload" size={12} /> Pre-Departure</>)}
                  {selectedQuery.phase === 'pre-arrival' && (<><Icon name="Send" size={12} /> Pre-Arrival</>)}
                  {selectedQuery.phase === 'post-arrival' && (<><Icon name="Package" size={12} /> Post-Arrival</>)}
                </Badge>
              </Inline>
              {selectedQuery.blocking && (
                <Muted style={{ color: theme.colors.errorDark, fontSize: '12px', fontWeight: 500, marginTop: '8px', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Icon name="AlertTriangle" size={12} /> This query blocks booking and dispatch. Resolution is critical.
                </Muted>
              )}
              <Divider />
              <Label>Query Details</Label>
              <div>{selectedQuery.details}</div>
              <Divider />
              <Label>Legal Context</Label>
              <Muted>
                Your response will be logged as an official amendment to the export declaration and submitted to {authority.code} for assessment. Keep explanations clear and factual.
              </Muted>
            </div>
            <div>
              <Label>Required to Resolve</Label>
              {/* NOTE: pre-existing — this checklist matches against hardcoded
                  demo query IDs that never occur since `queries` above is
                  always []. Out of scope for this visual pass; flagged only. */}
              <Checklist>
                {selectedQuery.queryId === 'QRY-ICE-88231' && (
                  <>
                    <Inline>
                      <input type="checkbox" disabled={!canUploadDocs} />
                      <span>Upload revised Commercial Invoice</span>
                    </Inline>
                    <Inline>
                      <input type="checkbox" />
                      <span>Add explanation note (brief & factual)</span>
                    </Inline>
                    <Inline>
                      <input type="checkbox" />
                      <span>Confirm corrected HS classification</span>
                    </Inline>
                  </>
                )}
                {selectedQuery.queryId === 'QRY-ICE-88245' && (
                  <>
                    <Inline>
                      <input type="checkbox" disabled={!canUploadDocs} />
                      <span>Upload valuation worksheet or supplier confirmation</span>
                    </Inline>
                    <Inline>
                      <input type="checkbox" />
                      <span>Add justification note</span>
                    </Inline>
                  </>
                )}
              </Checklist>
              <Divider />
              <Label>Submission Preview</Label>
              <NoteCard>
                <Muted>This is what will be sent to the authority. Review for accuracy.</Muted>
                <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', color: theme.colors.gray900, wordWrap: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
{`{
  "queryId": "${selectedQuery.queryId}",
  "documents": ["Commercial Invoice v3"],
  "changes": { "hsCode": "62063010" },
  "notes": "Clarified as requested"
}`}
                </pre>
              </NoteCard>
              <Inline style={{ marginTop: 12, gap: 8 }}>
                <Button size="sm" variant="ghost" style={{ background: theme.colors.infoLight, color: theme.colors.infoDark, border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled={!canView} title={!canView ? 'Compliance role required' : ''}>
                  <Icon name="Search" size={14} /> View Authority Details
                </Button>
                <Button size="sm" disabled={!canRespond} title={!canRespond ? 'Only Compliance can submit to customs authority' : ''} onClick={() => {
                  if (canRespond) {
                    logAudit('customs.respond', userRole, { queryId: selectedQuery.queryId }, { status: 'responded', authority: authority.code });
                    setShowWorkspace(false);
                  }
                }}>Submit to {authority.code}</Button>
              </Inline>
              {!canRespond && (
                <Muted style={{ marginTop: 8 }} title="This action requires Compliance role due to authority submission rules.">
                  Handled by Compliance · You'll be notified as soon as the authority responds.
                </Muted>
              )}
            </div>
          </Workspace>
        </Card>
      )}

      <Card>
        <SectionHeading icon="Lock">Notes (visibility-safe)</SectionHeading>
        <TwoCol>
          {visibleNotes.map((note) => (
            <NoteCard key={note.id}>
              <Inline>
                <strong>{note.author}</strong>
                <Audience>{note.audience === 'internal' ? 'Internal' : 'Customer'}</Audience>
                <StatusBadge variant={note.category === 'blocking' ? 'error' : 'info'} label={note.category === 'blocking' ? 'Blocking' : 'Info'} />
                <StatusBadge
                  variant={note.noteType === 'customer-safe' ? 'approved' : note.noteType === 'legal' ? 'warning' : note.noteType === 'system' ? 'neutral' : 'info'}
                  label={note.noteType === 'customer-safe' ? 'Customer-safe' : note.noteType === 'system' ? 'System' : note.noteType === 'legal' ? 'Legal' : 'Internal'}
                />
              </Inline>
              <div>{note.summary}</div>
            </NoteCard>
          ))}
        </TwoCol>
        {userRole === 'customer' && <Muted>Raw ICEGATE codes and officer remarks are hidden. You see a safe summary only.</Muted>}
      </Card>
        </LayoutMain>

        {/* Right Context Rail */}
        <LayoutSide $top={railTop} ref={railRef}>
          <CardContainer variant="elevated" padding="md">
            <div style={{ fontWeight: 600, fontSize: 14, color: theme.colors.gray800, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="Users" size={14} /> People Involved
            </div>
            {contacts.complianceOfficer && (
              <div style={{
                padding: 12,
                border: `1px solid ${theme.colors.borderSubtle}`,
                borderRadius: 8,
                background: theme.colors.background,
              }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', color: theme.colors.gray500, fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Icon name="Shield" size={12} /> Compliance Officer
                </div>
                <ContactCard
                  contact={contacts.complianceOfficer}
                  onAction={(action) => {
                    if (action === 'message') {
                      onOpenMessaging?.({
                        orderId: selectedOrder?.orderId,
                        targetContactId: Number(contacts.complianceOfficer?.id || 0) || undefined,
                        targetContactName: contacts.complianceOfficer?.name,
                        targetContactEmail: contacts.complianceOfficer?.email,
                        preferredSurface: 'drawer',
                      });
                    }
                  }}
                />
              </div>
            )}
            <ContactPanel
              title="External (Broker)"
              icon="User"
              contacts={contacts.broker ? [contacts.broker] : []}
              onContactAction={(contact, action) => {
                if (action === 'message') {
                  onOpenMessaging?.({
                    orderId: selectedOrder?.orderId,
                    targetContactId: Number(contact.id || 0) || undefined,
                    targetContactName: contact.name,
                    targetContactEmail: contact.email,
                    preferredSurface: 'drawer',
                  });
                }
              }}
            />
          </CardContainer>
        </LayoutSide>
      </LayoutGridComponent>
    </Page>

    {/* Full Declaration Modal */}
    {showFullDeclaration && (
      <Modal onClick={() => setShowFullDeclaration(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="FileText" size={20} /> Full Export Declaration
            </ModalTitle>
            <Button size="sm" variant="ghost" onClick={() => setShowFullDeclaration(false)}><Icon name="X" size={16} /></Button>
          </ModalHeader>
          <ModalBody>
            <Grid>
              <div>
                <Label>Shipment Reference</Label>
                <Value>{selectedOrder.orderId}</Value>
              </div>
              <div>
                <Label>Export Declaration Ref</Label>
                <Value>{selectedRecord?.bankMetadata?.bankSubmissionRef || '—'}</Value>
              </div>
              <div>
                <Label>Authority Reference</Label>
                <Value>{selectedRecord?.bankMetadata?.femaReference || '—'}</Value>
              </div>
              <div>
                <Label>Filing Mode</Label>
                <Value>API (Automated)</Value>
              </div>
              <div>
                <Label>Exporter IEC</Label>
                <Value>{selectedRecord?.id || '—'}</Value>
              </div>
              <div>
                <Label>Exporter Name</Label>
                <Value>{selectedRecord?.supplier || '—'}</Value>
              </div>
              <div>
                <Label>Importer</Label>
                <Value>{selectedRecord?.buyer || '—'}</Value>
              </div>
              <div>
                <Label>Importer Address</Label>
                <Value>Available in linked customer profile</Value>
              </div>
              <div>
                <Label>Port of Loading</Label>
                <Value>Loaded from shipment leg</Value>
              </div>
              <div>
                <Label>Port of Discharge</Label>
                <Value>Loaded from shipment leg</Value>
              </div>
              <div>
                <Label>Country of Destination</Label>
                <Value>{selectedRecord?.country || '—'}</Value>
              </div>
              <div>
                <Label>Incoterms</Label>
                <Value>{selectedRecord?.incoterms || '—'}</Value>
              </div>
              <div>
                <Label>Currency</Label>
                <Value>{selectedRecord?.currency || '—'}</Value>
              </div>
              <div>
                <Label>Total FOB Value</Label>
                <Value>{totalValueDisplay}</Value>
              </div>
              <div>
                <Label>Freight</Label>
                <Value>Calculated in shipment pricing</Value>
              </div>
              <div>
                <Label>Insurance</Label>
                <Value>Calculated in shipment pricing</Value>
              </div>
              <div>
                <Label>HS Codes</Label>
                <Value>Refer approved checklist attachments</Value>
              </div>
              <div>
                <Label>Drawback Claimed</Label>
                <Value>Refer customs filing outcome</Value>
              </div>
              <div>
                <Label>GST Status</Label>
                <Value>Refer tax filing outcome</Value>
              </div>
              <div>
                <Label>Carrier</Label>
                <Value>{selectedOrder.carrier}</Value>
              </div>
              <div>
                <Label>Vessel Name</Label>
                <Value>{selectedOrder.vessel}</Value>
              </div>
              <div>
                <Label>Expected Sailing</Label>
                <Value>{selectedOrder.eta || 'TBD'}</Value>
              </div>
            </Grid>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="outline" onClick={() => setShowFullDeclaration(false)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )}

    {/* Authority Details Modal */}
    {showAuthorityDetails && (
      <Modal onClick={() => setShowAuthorityDetails(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="Search" size={20} /> {authority.code} Authority Details
            </ModalTitle>
            <Button size="sm" variant="ghost" onClick={() => setShowAuthorityDetails(false)}><Icon name="X" size={16} /></Button>
          </ModalHeader>
          <ModalBody>
            <Grid>
              <div>
                <Label>Authority Code</Label>
                <Value>{authority.code}</Value>
              </div>
              <div>
                <Label>Country</Label>
                <Value>{authority.countryFlag} {authority.country}</Value>
              </div>
              <div>
                <Label>Filing Mode</Label>
                <Value>{authority.filingMode}</Value>
              </div>
              <div>
                <Label>Declaration Type</Label>
                <Value>Shipping Bill (Export)</Value>
              </div>
              <div>
                <Label>Supports Amendments</Label>
                <Value style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {authority.capabilities.amendments ? <Icon name="CheckCircle" size={14} /> : <Icon name="XCircle" size={14} />} {authority.capabilities.amendments ? 'Yes' : 'No'}
                </Value>
              </div>
              <div>
                <Label>Async Queries</Label>
                <Value style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {authority.capabilities.asyncQueries ? <Icon name="CheckCircle" size={14} /> : <Icon name="XCircle" size={14} />} {authority.capabilities.asyncQueries ? 'Supported' : 'Not Supported'}
                </Value>
              </div>
              <div>
                <Label>Partial Visibility</Label>
                <Value style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {authority.capabilities.partialVisibility ? <Icon name="CheckCircle" size={14} /> : <Icon name="XCircle" size={14} />} {authority.capabilities.partialVisibility ? 'Enabled' : 'Disabled'}
                </Value>
              </div>
              <div>
                <Label>Portal URL</Label>
                <Value><a href="https://www.icegate.gov.in" target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.infoDark }}>https://www.icegate.gov.in</a></Value>
              </div>
              <div>
                <Label>Help Desk</Label>
                <Value>1800-1234-5678</Value>
              </div>
              <div>
                <Label>Email Support</Label>
                <Value>support@icegate.gov.in</Value>
              </div>
              <div>
                <Label>Operating Hours</Label>
                <Value>24/7 (Automated System)</Value>
              </div>
              <div>
                <Label>SLA for Queries</Label>
                <Value>48-72 hours (business days)</Value>
              </div>
            </Grid>
            <div style={{ marginTop: '24px', padding: '16px', background: theme.colors.background, borderRadius: '8px' }}>
              <Label style={{ marginBottom: '8px' }}>Authority-Specific Notes</Label>
              <Muted>ICEGATE is the Indian Customs EDI Gateway. All export declarations (Shipping Bills) must be filed electronically. LEO (Let Export Order) is required before cargo can be loaded. Amendments are permitted within 24 hours of filing if no assessment has begun.</Muted>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="outline" onClick={() => setShowAuthorityDetails(false)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )}

    {/* Upload/Replace Document Modal */}
    {showUploadModal && selectedDocument && (
      <Modal onClick={() => { setShowUploadModal(false); setSelectedDocument(null); setSelectedUploadFile(null); }}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="Upload" size={20} /> {selectedDocument.status === 'pending' ? 'Upload' : 'Replace'} {selectedDocument.name}
            </ModalTitle>
            <Button size="sm" variant="ghost" onClick={() => { setShowUploadModal(false); setSelectedDocument(null); setSelectedUploadFile(null); }}><Icon name="X" size={16} /></Button>
          </ModalHeader>
          <ModalBody>
            <Grid>
              <div>
                <Label>Required Document</Label>
                <Value>{selectedDocument.name}</Value>
              </div>
              <div>
                <Label>Shipment ID</Label>
                <Value>{selectedOrder.orderId}</Value>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Label>Select File</Label>
                <label style={{
                  display: 'block',
                  border: `2px dashed ${theme.colors.border}`,
                  borderRadius: '8px',
                  padding: '40px',
                  textAlign: 'center',
                  background: selectedUploadFile ? theme.colors.successLight : theme.colors.background,
                  marginTop: '8px',
                  cursor: 'pointer'
                }}>
                  <input type="file" style={{ display: 'none' }} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setSelectedUploadFile(file);
                  }} />
                  <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>
                    {selectedUploadFile ? <Icon name="CheckCircle" size={24} /> : <Icon name="FileText" size={24} />}
                  </div>
                  <Value>{selectedUploadFile ? selectedUploadFile.name : 'Click to select a file or drag and drop'}</Value>
                  {!selectedUploadFile && <Muted>Supports PDF, DOCX, JPG (Max 10MB)</Muted>}
                </label>
              </div>
            </Grid>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="outline" onClick={() => { setShowUploadModal(false); setSelectedDocument(null); setSelectedUploadFile(null); }}>Cancel</Button>
            <Button size="sm" disabled={!selectedUploadFile} onClick={() => { 
                setShowUploadModal(false); 
                setSelectedDocument(null);
                setSelectedUploadFile(null);
                alert('File upload API integration pending'); 
            }}>Complete Upload</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )}

    {/* Document Viewer Modal */}
    {showDocumentViewer && selectedDocument && (
      <Modal onClick={() => { setShowDocumentViewer(false); setSelectedDocument(null); }}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="FileText" size={20} /> {selectedDocument.name}
            </ModalTitle>
            <Button size="sm" variant="ghost" onClick={() => { setShowDocumentViewer(false); setSelectedDocument(null); }}><Icon name="X" size={16} /></Button>
          </ModalHeader>
          <ModalBody>
            <Grid>
              <div>
                <Label>Document Name</Label>
                <Value>{selectedDocument.name}</Value>
              </div>
              <div>
                <Label>Version</Label>
                <Value>{selectedDocument.version}</Value>
              </div>
              <div>
                <Label>Status</Label>
                <Value>
                  <StatusBadge variant={DOC_STATUS_BADGE[selectedDocument.status].variant} label={DOC_STATUS_BADGE[selectedDocument.status].label} />
                </Value>
              </div>
              <div>
                <Label>Used in Filing</Label>
                <Value style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {selectedDocument.usedInFiling ? <Icon name="CheckCircle" size={14} /> : <Icon name="XCircle" size={14} />} {selectedDocument.usedInFiling ? 'Yes' : 'No'}
                </Value>
              </div>
              <div>
                <Label>Triggered By</Label>
                <Value>
                  {selectedDocument.triggeredBy === 'query' && 'Query Response'}
                  {selectedDocument.triggeredBy === 'voluntary' && 'Voluntary Upload'}
                  {selectedDocument.triggeredBy === 'system' && 'System Required'}
                </Value>
              </div>
              <div>
                <Label>Linked Query</Label>
                <Value>{selectedDocument.linkedQueryId || 'None'}</Value>
              </div>
            </Grid>
            <div style={{ marginTop: '24px', padding: '16px', background: theme.colors.background, borderRadius: '8px', textAlign: 'center' }}>
              <Muted style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                <Icon name="FileText" size={14} /> Document preview would appear here
              </Muted>
              <div style={{ marginTop: '16px', padding: '80px', background: theme.colors.surface, border: `2px dashed ${theme.colors.borderSubtle}`, borderRadius: '8px' }}>
                <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>
                  <Icon name="FileText" size={48} />
                </div>
                <Value>{selectedDocument.name}</Value>
                <Muted style={{ display: 'block', marginTop: '8px' }}>PDF viewer integration pending</Muted>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="outline" onClick={() => { setShowDocumentViewer(false); setSelectedDocument(null); }}>Close</Button>
            <Button size="sm">Download</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )}

    {/* Version History Modal */}
    {showVersionHistory && selectedDocument && (
      <Modal onClick={() => { setShowVersionHistory(false); setSelectedDocument(null); }}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon name="History" size={20} /> Version History: {selectedDocument.name}
            </ModalTitle>
            <Button size="sm" variant="ghost" onClick={() => { setShowVersionHistory(false); setSelectedDocument(null); }}><Icon name="X" size={16} /></Button>
          </ModalHeader>
          <ModalBody>
            {/* NOTE: pre-existing — v1/v2/v3 dates and authors below are
                placeholder content, not backed by real version data. Out of
                scope for this visual pass; flagged only. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <TimelineItem>
                <TimelineHeader>
                  <div>
                    <TimelineTitle>{selectedDocument.version} (Current)</TimelineTitle>
                    <TimelineLabel>Jan 26, 2026 - 15:40 PM</TimelineLabel>
                  </div>
                  <StatusBadge variant="filed" label="Active" />
                </TimelineHeader>
                <TimelineValue>
                  <Muted>Uploaded by: {`Compliance - ${complianceOwnerName}`}</Muted>
                  <div>Updated to address query {selectedDocument.linkedQueryId}</div>
                  <Inline style={{ marginTop: 8 }}>
                    <Button size="sm" variant="ghost">View</Button>
                    <Button size="sm" variant="ghost">Download</Button>
                  </Inline>
                </TimelineValue>
              </TimelineItem>

              <TimelineItem>
                <TimelineHeader>
                  <div>
                    <TimelineTitle>v2</TimelineTitle>
                    <TimelineLabel>Jan 25, 2026 - 10:22 AM</TimelineLabel>
                  </div>
                  <StatusBadge variant="neutral" label="Superseded" />
                </TimelineHeader>
                <TimelineValue>
                  <Muted>Uploaded by: {`Compliance - ${complianceOwnerName}`}</Muted>
                  <div>Initial filing version</div>
                  <Inline style={{ marginTop: 8 }}>
                    <Button size="sm" variant="ghost">View</Button>
                    <Button size="sm" variant="ghost">Download</Button>
                  </Inline>
                </TimelineValue>
              </TimelineItem>

              <TimelineItem>
                <TimelineHeader>
                  <div>
                    <TimelineTitle>v1</TimelineTitle>
                    <TimelineLabel>Jan 24, 2026 - 14:15 PM</TimelineLabel>
                  </div>
                  <StatusBadge variant="pending" label="Draft" />
                </TimelineHeader>
                <TimelineValue>
                  <Muted>Uploaded by: Manufacturer</Muted>
                  <div>Initial draft (not used in filing)</div>
                  <Inline style={{ marginTop: 8 }}>
                    <Button size="sm" variant="ghost">View</Button>
                    <Button size="sm" variant="ghost">Download</Button>
                  </Inline>
                </TimelineValue>
              </TimelineItem>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="outline" onClick={() => { setShowVersionHistory(false); setSelectedDocument(null); }}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )}
    </>
  );
};
